/* eslint-disable camelcase */
import { OptionType, SORT_ORDER, TableSortOption } from '@evinced-private/ui-common';

import { MOBILE_APP_ID_FILTER_ID } from 'src/apps/dashboard/constants/MobileDashboard.constants';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import {
	ConditionInt,
	Mobile_Core_Label_IssuesSortFields,
	Mobile_Core_Label_IssuesSortOrder,
	Mobile_Core_Label_IssuesTextSearchFields
} from 'src/common/types/graphql.generated';

import { buildDashboardIssuesSortParams } from '../../helpers/issueSortVariableBuilder';
import { MobileCoreLabelAllIssuesQueryVariables } from '../MobileCoreLabelIssues.generated';

import { hasSelectedBusinessUnit } from './mobileFilterVariableBuilder';

const sortFieldsMap: Record<string, Mobile_Core_Label_IssuesSortFields> = {
	issueType: Mobile_Core_Label_IssuesSortFields.TypeName,
	severity: Mobile_Core_Label_IssuesSortFields.SeverityWeight,
	severityName: Mobile_Core_Label_IssuesSortFields.SeverityName,
	appId: Mobile_Core_Label_IssuesSortFields.AppId,
	elementDescription: Mobile_Core_Label_IssuesSortFields.ElementDescription,
	foundBy: Mobile_Core_Label_IssuesSortFields.ProductName,
	sessionOrTest: Mobile_Core_Label_IssuesSortFields.SessionName
};

const sortOrderMap: Record<SORT_ORDER, Mobile_Core_Label_IssuesSortOrder> = {
	[SORT_ORDER.ASC]: Mobile_Core_Label_IssuesSortOrder.Ascending,
	[SORT_ORDER.DESC]: Mobile_Core_Label_IssuesSortOrder.Descending
};

const buildDaysFilter = (dateRangeOptions?: OptionType[]): ConditionInt | undefined => {
	if (!dateRangeOptions?.length) {
		return { value: [30] };
	}
	const days = Number(dateRangeOptions[0].value);
	return Number.isNaN(days) ? { value: [30] } : { value: [days] };
};

export const buildMobileCoreLabelIssuesVariables = (
	filters: IFilterOptions,
	page: number,
	sizePerPage: number,
	sort?: TableSortOption,
	mobileBuIds?: string[]
): MobileCoreLabelAllIssuesQueryVariables => {
	const variablesBuilder = new QueryVariablesBuilder<MobileCoreLabelAllIssuesQueryVariables>({
		sortFieldsMap,
		sortOrderMap
	});

	variablesBuilder.addPaginationParams(page, sizePerPage);

	const { sort: effectiveSort, sortFields } = buildDashboardIssuesSortParams(sort, true);
	variablesBuilder.addSortParam(effectiveSort, sortFields);

	const daysFilter = buildDaysFilter(filters.dateRange);
	if (daysFilter) {
		variablesBuilder.addConditionParam('daysFilter', daysFilter.value);
	}

	variablesBuilder
		.addFilterParam('severityFilter', filters.severities)
		.addFilterParam('issueTypeFilter', filters.types);

	if (filters[MOBILE_APP_ID_FILTER_ID]?.length) {
		variablesBuilder.addFilterParam('appIdFilter', filters[MOBILE_APP_ID_FILTER_ID]);
	}

	if (hasSelectedBusinessUnit(filters.unitId)) {
		variablesBuilder.addFilterParam('businessUnitFilter', filters.unitId);
	} else if (mobileBuIds?.length) {
		variablesBuilder.addConditionParam('businessUnitFilter', mobileBuIds);
	}

	const variables = variablesBuilder.getVariables();

	if (filters.searchValue) {
		const searchValue = filters.searchValue.toString().trim();
		if (searchValue) {
			variables.textSearch = {
				searchFields: [
					Mobile_Core_Label_IssuesTextSearchFields.TypeName,
					Mobile_Core_Label_IssuesTextSearchFields.AppId,
					Mobile_Core_Label_IssuesTextSearchFields.ElementDescription,
					Mobile_Core_Label_IssuesTextSearchFields.SessionName
				],
				searchValues: [searchValue],
				insensitive: true
			};
		}
	}

	return variables;
};
